// apps/web/src/features/russia-requests/components/editor/RussiaEditorShell.tsx
//
// Shared host shell for the russia-rates editor — used by both the
// manual-create surface (`/russia-rates/new`) and the draft-edit surface
// (`/russia-rates/:id/edit`, #1571). Owns the RHF form, the persisted-id
// baton, the discard-cleanup wiring, and the `<EditorDrawer>` + body
// composition. The two surfaces differ only in their initial state
// (defaults, seeded positions, persisted id), which the host passes in.
//
// @aiInvariant `EditorDrawer` reads `dirty` from `formState.isDirty` so the
//   shared unsaved-changes guard catches Escape/backdrop/Back. The X and
//   Cancel buttons go through `useEditorGuard().confirmDestructive` in the
//   body so the same prompt fires for in-form close paths too.

import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateRussiaRequestManualInputSchema,
  RussiaRequestKind,
  type CreateRussiaRequestManualInput,
} from '@magimp/shared';
import { EditorDrawer } from '../../../../ui/index.js';
import { cleanupNeverSavedPositionPhotos } from '../../../../api/russia-requests/items.js';
import { RussiaEditorBody } from './RussiaEditorBody.js';
import type { RussiaPositionRow } from './russiaPositions.model.js';

export interface RussiaEditorShellProps {
  /** SIMPLE shows the positions card; COMPLEX shows totals + files. */
  kind: RussiaRequestKind;
  /** RHF default values to seed the form (create empties, or the loaded row). */
  defaults: CreateRussiaRequestManualInput;
  /** Existing row id in edit mode; null on create until the first persist. */
  initialPersistedId: string | null;
  /** Already-persisted SIMPLE positions to seed in edit mode (#1571). */
  initialPositions?: RussiaPositionRow[] | undefined;
  /** Switches editor-mode copy (title / save labels) to the edit variant. */
  editMode: boolean;
}

export function RussiaEditorShell({
  kind,
  defaults,
  initialPersistedId,
  initialPositions,
  editMode,
}: RussiaEditorShellProps) {
  const navigate = useNavigate();

  // The positions card registers its never-saved-photo-key collector here so
  //   the confirmed discard-close can GC the orphaned upload objects (#1561).
  const collectNeverSavedPhotoKeysRef = useRef<(() => Promise<string[]>) | null>(null);

  // WHY (#1561): BOTH close affordances route through `close` — see the
  //   create host's note. Cleanup is best-effort and navigation is NOT
  //   blocked on it. In edit mode the seeded existing photos NEVER enter the
  //   never-saved accumulator (see useRussiaDraftPositions seeding), so this
  //   only ever GCs genuinely orphaned session uploads, never saved photos.
  const close = useCallback(() => {
    const collect = collectNeverSavedPhotoKeysRef.current;
    if (collect) {
      void collect()
        .then((keys) => {
          if (keys.length > 0) {
            return cleanupNeverSavedPositionPhotos({ objectKeys: keys }).catch(() => undefined);
          }
          return undefined;
        })
        .catch(() => undefined);
    }
    navigate('/russia-rates');
  }, [navigate]);

  const form = useForm<CreateRussiaRequestManualInput>({
    resolver: zodResolver(CreateRussiaRequestManualInputSchema),
    // WHY: `mode: 'all'` keeps `formState.isValid` reactive to the current
    //   payload while still doing blur-time per-field validation.
    mode: 'all',
    defaultValues: defaults,
  });

  const [persistedId, setPersistedId] = useState<string | null>(initialPersistedId);
  const [submitting, setSubmitting] = useState(false);
  const [positionsDirty, setPositionsDirty] = useState(false);

  return (
    <EditorDrawer
      dirty={form.formState.isDirty || positionsDirty}
      busy={submitting}
      onConfirmClose={close}
      width={640}
      narrow={false}
    >
      <RussiaEditorBody
        form={form}
        kind={kind}
        editMode={editMode}
        persistedId={persistedId}
        setPersistedId={setPersistedId}
        initialPositions={initialPositions}
        submitting={submitting}
        setSubmitting={setSubmitting}
        onClose={close}
        onNavigateToRow={(id) => navigate(`/russia-rates/${encodeURIComponent(id)}`)}
        onPositionsDirtyChange={setPositionsDirty}
        onRegisterCollectNeverSavedPhotoKeys={(collect) => {
          collectNeverSavedPhotoKeysRef.current = collect;
        }}
      />
    </EditorDrawer>
  );
}
